"use client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { replyLikeAction } from "@/actions/forum.action";
import HeartIcon from "@/components/icons/heart";
import { Button } from "@/components/ui/button";
import type { ApiResponse } from "@/lib/fetch";

interface Props {
  replyId: string;
  initialCount: number;
  initialLiked: boolean;
  queryKey: string[];
}

const TopicReplyLikeButton = ({
  replyId,
  initialCount,
  initialLiked,
  queryKey,
}: Props) => {
  const [liked, setLiked] = useState(initialLiked);
  const [count, setCount] = useState(initialCount);
  const queryClient = useQueryClient();

  type Snapshot = { count: number; liked: boolean };

  const { mutate, isPending } = useMutation<
    ApiResponse<unknown>,
    Error,
    boolean,
    Snapshot
  >({
    mutationFn: (currentLiked: boolean) => replyLikeAction(replyId, currentLiked),
    onMutate: (currentLiked) => {
      const snapshot = { count, liked };
      const newLiked = !currentLiked;
      setLiked(newLiked);
      setCount((c) => (newLiked ? c + 1 : c - 1));
      return snapshot;
    },
    onError: (_err, _vars, snapshot) => {
      if (snapshot) {
        setLiked(snapshot.liked);
        setCount(snapshot.count);
      }
    },
    onSuccess: (res, _vars, snapshot) => {
      if (!res.isSuccess && snapshot) {
        setLiked(snapshot.liked);
        setCount(snapshot.count);
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return (
    <span className="inline-flex items-center gap-0.5">
      <Button
        className="group"
        disabled={isPending}
        onClick={() => mutate(liked)}
        size="icon-sm"
        variant="ghost"
      >
        <HeartIcon
          className={
            liked
              ? "size-5 fill-brand-blue-500"
              : "size-5 fill-neutral-300 group-hover:fill-brand-blue-200"
          }
          fill="currentColor"
        />
      </Button>
      <span className="text-xs text-neutral-500">{count}</span>
    </span>
  );
};

export default TopicReplyLikeButton;
